import React, { useEffect, useState } from "react";
import axios from "axios";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Heart, Quote } from "lucide-react";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import PartnersMarquee from "@/components/PartnersMarquee";
import StatsCard from "@/components/StatsCard";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import Img from "@/components/Img";
import { slotKeyFor } from "@/data/imageSlots";
import {
  HERO_IMAGES, SHOWCASE_CATEGORIES, SERVICES, VENUES as STATIC_VENUES, CELEBRATION_TYPES,
  TESTIMONIALS, STATS, WHY_US, PROCESS,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PageHeading = ({ overline, title, em, sub, align = "left", dark = false }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {overline && <p className="pill" data-testid="section-overline">{overline}</p>}
    <h2 className={`font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] mt-5 ${dark ? "text-[#FAF6F1]" : "text-[#3B312D]"}`}>
      {title} {em && <span className={`italic ${dark ? "text-[#F4DCD8]" : "text-[#B65980]"}`}>{em}</span>}
    </h2>
    {sub && <p className={`mt-5 text-[16px] md:text-[17px] leading-relaxed ${dark ? "text-[#FAF6F1]/85" : "text-[#3B312D]/80"}`}>{sub}</p>}
  </div>
);

const PetalOrnament = ({ className = "", color = "#B65980" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none" stroke={color} strokeWidth="0.8" aria-hidden>
    <path d="M100 20 C 60 60, 60 140, 100 180 C 140 140, 140 60, 100 20 Z" opacity="0.35"/>
    <path d="M30 100 C 60 80, 140 80, 170 100 C 140 120, 60 120, 30 100 Z" opacity="0.25"/>
    <circle cx="100" cy="100" r="6" fill={color} opacity="0.5" stroke="none"/>
  </svg>
);

export default function Home() {
  const [venues, setVenues] = useState(STATIC_VENUES);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/venues`);
        if (mounted && Array.isArray(data) && data.length > 0) setVenues(data);
      } catch (e) {
        // keep static fallback
      }
    })();
    return () => { mounted = false; };
  }, []);
  return (
    <div data-testid="page-home" className="bg-[#FAF6F1]">
      
      <SEO
        title="Luxury Wedding & Event Planners in Gurgaon, Chandigarh & Across India"
        description="Petals & Promises is a luxury wedding and event planning company creating elegant weddings, intimate celebrations, corporate events, and devotional gatherings across North India. Studios in Gurgaon, Chandigarh, and Bangalore."
        path="/"
        keywords="wedding planner Gurgaon, wedding planner Chandigarh, luxury wedding planner India, destination wedding planner, corporate event management Gurgaon, event management company NCR, devotional event planner, mata ki chowki planner, jagran planner, premium event company"
      />
{/* HERO */}
      <section className="relative min-h-[92vh] md:min-h-[100vh] w-full overflow-hidden flex items-center">
        <Img imageKey="home.hero.1" defaultSrc={HERO_IMAGES[0]} defaultAlt="Luxury wedding reception"
             className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3B312D]/25 via-[#3B312D]/30 to-[#3B312D]/65" />
        <PetalOrnament className="absolute -top-20 -right-20 w-[380px] h-[380px] opacity-20" color="#F4DCD8" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-0">
          <Reveal>
            <span className="pill-rose !bg-white/15 !border-[#F4DCD8]/45 !text-[#F4DCD8] backdrop-blur-md">
              <Heart size={12} /> Luxury Wedding & Event Planners
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display text-white text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] leading-[1.0] mt-6 max-w-5xl drop-shadow-[0_4px_24px_rgba(0,0,0,0.3)]">
              Where Every Celebration<br className="hidden md:block"/>
              Becomes a <span className="italic text-[#F4DCD8]">Beautiful Promise</span>.
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-6 text-white text-[16px] md:text-[18px] max-w-2xl leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)]">
              Petals &amp; Promises is a luxury wedding and event planning company creating elegant weddings, intimate celebrations,
              corporate events, and devotional gatherings across North India. From concept and décor to guest experience and flawless
              execution, we turn every occasion into a beautifully planned celebration.
            </p>
          </Reveal>
          <Reveal delay={360}>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="hero-plan-event">
                Plan Your Celebration <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="btn-ghost !text-white !border-white/70 hover:!bg-white hover:!text-[#B65980]" data-testid="hero-explore-services">
                Explore Our Services
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS — refined horizontal blush band */}
      <StatsCard stats={STATS} />

      {/* SHOWCASE CATEGORIES — equal-height grid */}
      <section className="py-20 md:py-28 bg-[#FAF6F1] relative overflow-hidden">
        <PetalOrnament className="absolute -top-10 -left-10 w-64 h-64 opacity-20" color="#8A9A7B" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <div className="flex items-end justify-between flex-wrap gap-8">
            <PageHeading overline="What We Craft" title="Six worlds, one" em="standard of care" />
            <Link to="/services" className="link-gold text-[13px] tracking-[0.22em] uppercase text-[#B65980] font-semibold" data-testid="showcase-view-all">
              View all services <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SHOWCASE_CATEGORIES.map((c, i) => (
              <CategoryCard key={c.title} c={c} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-20 md:py-28 bg-[#F4DCD8]/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <div className="rounded-[22px] overflow-hidden image-zoom h-[420px] md:h-[540px]">
              <Img
                imageKey="home.philosophy"
                defaultSrc="https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=1400&q=80"
                defaultAlt="Petals & Promises Indian wedding decor styling"
                className="w-full h-full"
              />
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <PageHeading overline="Our Philosophy" title="A creative-led team for" em="beautifully planned celebrations." sub="Petals & Promises brings together creative direction, elegant décor planning, vendor coordination, and on-ground event execution for weddings, private celebrations, corporate events, and devotional gatherings. Every detail is shaped to feel personal, graceful, and effortless." />
            <Link to="/about" className="btn-primary mt-8" data-testid="about-learn-more">Read Our Story <ArrowRight size={14} /></Link>
          </Reveal>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="py-20 md:py-28 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <PageHeading overline="Featured Services" title="End-to-end wedding and event planning," em="beautifully managed." sub="From luxury weddings and private celebrations to corporate events and devotional gatherings, Petals & Promises brings creative direction, décor planning, vendor coordination, and on-ground execution together under one seamless experience." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={i * 70} className="lux-card overflow-hidden flex flex-col h-full">
                <div className="relative h-56 image-zoom shrink-0">
                  <Img imageKey={slotKeyFor.service(s)} defaultSrc={s.image} defaultAlt={s.title} className="absolute inset-0 w-full h-full" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h4 className="font-display text-2xl md:text-[26px] text-[#3B312D]">{s.title}</h4>
                  <p className="mt-3 text-[14.5px] text-[#3B312D]/80 leading-relaxed flex-1">{s.summary}</p>
                  <Link to={`/services#${s.slug}`} className="link-gold mt-5 inline-flex text-[12px] tracking-[0.2em] uppercase text-[#B65980] font-semibold" data-testid={`featured-service-${s.slug}`}>
                    Explore Service <ArrowUpRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CELEBRATION TYPES */}
      <section className="py-20 md:py-28 bg-[#F4DCD8]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <PageHeading overline="Celebration Types" title="Every ritual," em="lovingly held." sub="From the first roka to the final reception celebration, we plan weddings, devotional gatherings, social milestones, and corporate experiences with care, culture, and thoughtful detail." />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {CELEBRATION_TYPES.slice(0, 12).map((c, i) => (
              <Reveal key={c.name} delay={i * 50} className="group relative rounded-2xl overflow-hidden image-zoom h-56">
                <Img imageKey={slotKeyFor.celebration(c)} defaultSrc={c.image} defaultAlt={c.name} className="absolute inset-0 w-full h-full" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3B312D]/88 via-[#3B312D]/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <h4 className="font-display text-xl md:text-2xl">{c.name}</h4>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/celebrations" className="btn-primary" data-testid="celebrations-view-all">All Celebrations <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* VENUE PREVIEW */}
      <section className="pt-16 md:pt-20 pb-20 md:pb-28 bg-[#3B312D] text-[#FAF6F1] grain-overlay relative overflow-hidden">
        <PetalOrnament className="absolute -bottom-10 -right-10 w-80 h-80 opacity-15" color="#C5A059" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <span className="pill-rose !bg-white/5 !border-[#F4DCD8]/40 !text-[#F4DCD8]">Venue Discovery</span>
              <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">
                Hand-picked <span className="italic text-[#C5A059]">venues</span><br/>across India &amp; beyond.
              </h2>
            </div>
            <Link to="/venues" className="link-gold text-[13px] tracking-[0.22em] uppercase text-[#C5A059] font-semibold" data-testid="venue-preview-all">
              Browse all venues <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
            {venues.slice(0, 8).map((v, i) => (
              <Reveal key={v.id || v.name} delay={i * 60} className="rounded-2xl overflow-hidden image-zoom relative h-64 md:h-72">
                <img src={v.image} alt={v.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3B312D]/90 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  {v.budget && <p className="text-[10px] tracking-[0.24em] uppercase text-[#C5A059] font-semibold">{v.budget}</p>}
                  <h4 className="font-display text-xl md:text-2xl mt-1 text-white">{v.name}</h4>
                  <p className="text-[12px] text-white/80 mt-1">{v.capacity || "On request"} guests · {v.io || "—"}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <PartnersMarquee />


      {/* PROCESS PREVIEW */}
      <section className="py-20 md:py-28 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <PageHeading overline="Our Process" title="Seven phases, one" em="calm partnership." sub="From discovery to post-event closure, every phase has named owners, written deliverables, and the right level of detail." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.slice(0, 4).map((p, i) => (
              <Reveal key={p.step} delay={i * 80} className="lux-card p-7 h-full">
                <p className="font-display text-5xl text-[#C5A059]/80 leading-none">{p.step}</p>
                <h4 className="font-display text-2xl mt-4 text-[#3B312D]">{p.title}</h4>
                <p className="mt-3 text-[14px] text-[#3B312D]/75 leading-relaxed">{p.desc}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/process" className="btn-ghost" data-testid="process-view-all">See All Seven Phases <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-[#F4DCD8]/30 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <PageHeading overline="Why Choose Us" title="Creative. Careful." em="Quietly precise." sub="From the first conversation to the final guest experience, we bring design thinking, calm planning, and careful coordination together so your celebration feels personal, graceful, and beautifully managed." />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={i * 90} className="lux-card p-7 h-full">
                <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">Why {i + 1}</p>
                <h4 className="font-display text-2xl md:text-[26px] mt-3 text-[#3B312D]">{w.title}</h4>
                <p className="mt-3 text-[14.5px] text-[#3B312D]/80 leading-relaxed">{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BROCHURE CTA */}
      <BrochureCTA />

      {/* TESTIMONIALS — premium carousel (3/2/1, autoplay, swipe) */}
      <section className="py-20 md:py-28 bg-[#3B312D] text-[#FAF6F1] grain-overlay">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <PageHeading overline="In Their Words" title="Trusted by" em="hosts who care." dark />
          <div className="mt-12">
            <TestimonialCarousel items={TESTIMONIALS} />
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

const CategoryCard = ({ c, i }) => (
  <Reveal delay={i * 80} className="relative group rounded-[22px] overflow-hidden image-zoom h-[380px] md:h-[420px]">
    <Img imageKey={slotKeyFor.showcase(c)} defaultSrc={c.image} defaultAlt={c.title} className="absolute inset-0 w-full h-full" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#3B312D]/92 via-[#3B312D]/25 to-[#3B312D]/10" />
    <div className="absolute inset-x-0 bottom-0 p-7 text-white">
      <h3 className="font-display text-3xl md:text-[32px] leading-tight">{c.title}</h3>
      <p className="mt-2 text-white/88 text-[14px] max-w-md leading-relaxed">{c.blurb}</p>
    </div>
  </Reveal>
);
